import React, { useState, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Login from '../Permissions/Login'
import SignUp from '../Permissions/SignUp'
import CompanyDetails from '../Pages/CompanyDetails/CompanyDetails'
import ResetPassword from '../Permissions/ResetPassword'
import PageRoutes from './PageRoutes'
import { getCompanySettings } from '../Api/CompanyApi'
import PrivacyPolicy from '../Pages/Public/PrivacyPolicy'
import TermsConditions from '../Pages/Public/TermsConditions'

const ProtectedRoutes = () => {
  const location = useLocation()

  // 1. Read auth status directly from localStorage to prevent state sync lag/glitches during transitions
  const isAuthenticated = !!(localStorage.getItem('token') || localStorage.getItem('authToken')) && localStorage.getItem('isAuthenticated') === 'true'

  // 2. Initialize hasCompany from localStorage to avoid loading flashes on app reload
  const [hasCompany, setHasCompany] = useState(() => {
    const cached = localStorage.getItem('hasCompany')
    if (cached === 'true') return true
    if (cached === 'false') return false
    return null
  })

  // 3. Determine if we are loading. We are only loading if authenticated, on a protected route, and we don't know the company status yet.
  const bypassPaths = ['/login', '/signup', '/reset-password', '/company-details']
  const isBypassPath = bypassPaths.includes(location.pathname)
  const isLoading = isAuthenticated && !isBypassPath && hasCompany === null

  useEffect(() => {
    // If not authenticated or on bypass path or already checked, no need to fetch
    if (!isAuthenticated || isBypassPath || hasCompany !== null) {
      return
    }

    const checkCompany = async () => {
      try {
        const res = await getCompanySettings()
        if (res && (res.success || res.id || res.data)) {
          setHasCompany(true)
          localStorage.setItem('hasCompany', 'true')
        } else {
          setHasCompany(false)
          localStorage.setItem('hasCompany', 'false')
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setHasCompany(false)
          localStorage.setItem('hasCompany', 'false')
        } else {
          // Fallback to true in case of server error so we don't block the user unnecessarily
          setHasCompany(true)
          localStorage.setItem('hasCompany', 'true')
        }
      }
    }

    checkCompany()
  }, [isAuthenticated, isBypassPath, hasCompany])

  // Public CMS Routes that can be opened in a new tab without auth/sidebar
  if (location.pathname === '/privacy-policy') {
    return <PrivacyPolicy />
  }
  if (location.pathname === '/terms-conditions') {
    return <TermsConditions />
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Loading...
      </div>
    )
  }

  // If authenticated user tries to access login/signup, redirect to dashboard or company-details
  if (isAuthenticated && (location.pathname === '/login' || location.pathname === '/signup')) {
    return hasCompany ? <Navigate to="/dashboard" replace /> : <Navigate to="/company-details" replace />
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Login />} />
      <Route path="/company-details" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={
        !isAuthenticated ? (
          <Navigate to="/login" replace />
        ) : !hasCompany ? (
          <Navigate to="/company-details" replace />
        ) : (
          <PageRoutes />
        )
      } />
    </Routes>
  );
}

export default ProtectedRoutes